import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Form, Modal, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axiosInstance from '../../../utils/axiosConfig';
import './VehicleVariants.css';

const VehicleVariants = () => {
    const { vehicleId } = useParams();
    const navigate = useNavigate();

    const [vehicle, setVehicle] = useState(null);
    const [variants, setVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingVariant, setEditingVariant] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        price: '',
        image_url: '',
        features: [],
        sort_order: 0
    });

    const [newFeature, setNewFeature] = useState('');
    const [errors, setErrors] = useState({});

    const validatePrice = (value) => {
        const priceRegex = /^[0-9,]+$/;
        return priceRegex.test(value);
    };

    const validateUrl = (value) => {
        if (!value) return true;
        try {
            new URL(value);
            return true;
        } catch {
            return false;
        }
    };

    const fetchVehicleData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/vehicles/${vehicleId}`);
            const vehicleData = response.data.data || response.data;
            setVehicle(vehicleData);
            setVariants(vehicleData.variants || []);
        } catch (error) {
            console.error('Error fetching vehicle:', error);
            toast.error('Failed to load vehicle data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVehicleData();
    }, [vehicleId]);

    const handleOpenModal = (variant = null) => {
        if (variant) {
            setEditingVariant(variant);
            setFormData({
                name: variant.name || '',
                price: variant.price || '',
                image_url: variant.image_url || '',
                features: variant.features || [],
                sort_order: variant.sort_order || 0
            });
        } else {
            setEditingVariant(null);
            setFormData({
                name: '',
                price: '',
                image_url: '',
                features: [],
                sort_order: 0
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingVariant(null);
        setNewFeature('');
        setErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        // Validate fields
        const newErrors = { ...errors };
        if (name === 'price') {
            if (value && !validatePrice(value)) {
                newErrors.price = 'Price must contain only numbers and commas (e.g., 47,000)';
            } else {
                delete newErrors.price;
            }
        }
        if (name === 'image_url') {
            if (value && !validateUrl(value)) {
                newErrors.image_url = 'Please enter a valid URL';
            } else {
                delete newErrors.image_url;
            }
        }
        setErrors(newErrors);
    };

    const handleAddFeature = () => {
        if (newFeature.trim()) {
            setFormData(prev => ({
                ...prev,
                features: [...prev.features, newFeature.trim()]
            }));
            setNewFeature('');
        }
    };

    const handleRemoveFeature = (index) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate before submit
        const newErrors = {};
        if (formData.price && !validatePrice(formData.price)) {
            newErrors.price = 'Price must contain only numbers and commas (e.g., 47,000)';
        }
        if (formData.image_url && !validateUrl(formData.image_url)) {
            newErrors.image_url = 'Please enter a valid URL';
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            toast.error('Please fix validation errors');
            return;
        }

        try {
            if (editingVariant) {
                await axiosInstance.put(`/admin/vehicles/${vehicle.id}/variants/${editingVariant.id}`, formData);
                toast.success('Variant updated successfully!');
            } else {
                await axiosInstance.post(`/admin/vehicles/${vehicle.id}/variants`, formData);
                toast.success('Variant added successfully!');
            }
            handleCloseModal();
            fetchVehicleData();
        } catch (error) {
            console.error('Error saving variant:', error);
            toast.error(error.response?.data?.message || 'Failed to save variant');
        }
    };

    const handleDelete = async (variantId) => {
        if (!window.confirm('Are you sure you want to delete this variant?')) return;

        try {
            await axiosInstance.delete(`/admin/vehicles/${vehicle.id}/variants/${variantId}`);
            toast.success('Variant deleted successfully!');
            fetchVehicleData();
        } catch (error) {
            console.error('Error deleting variant:', error);
            toast.error('Failed to delete variant');
        }
    };

    if (loading) {
        return <Container className="mt-5"><div className="text-center">Loading...</div></Container>;
    }

    return (
        <Container fluid className="mt-4">

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Button variant="outline-secondary" onClick={() => navigate(`/vehicle-management/view/${vehicleId}`)} className="me-2">
                        Back to Vehicle
                    </Button>
                    <h3 className="d-inline">Manage Variants - {vehicle?.title_en}</h3>
                </div>
                <Button variant="primary" onClick={() => handleOpenModal()}>
                    Add Variant
                </Button>
            </div>

            <Card>
                <Card.Body>
                    <Table responsive striped bordered hover>
                        <thead className="brand-table-head">
                            <tr>
                                <th>Name</th>
                                <th>Price (SAR)</th>
                                <th>Features</th>
                                <th>Sort Order</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {variants.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center">No variants found</td>
                                </tr>
                            ) : (
                                variants.map((variant) => (
                                    <tr key={variant.id}>
                                        <td>{variant.name}</td>
                                        <td>{variant.price}</td>
                                        <td>{variant.features?.length || 0} features</td>
                                        <td>{variant.sort_order}</td>
                                        <td>
                                            <Button variant="warning" size="sm" onClick={() => handleOpenModal(variant)} className="me-2">
                                                Edit
                                            </Button>
                                            <Button variant="danger" size="sm" onClick={() => handleDelete(variant.id)}>
                                                Delete
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleCloseModal} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingVariant ? 'Edit Variant' : 'Add Variant'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Variant Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="e.g., DZIRE CVT GL"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Price (SAR)</Form.Label>
                            <Form.Control
                                type="text"
                                name="price"
                                value={formData.price}
                                onChange={handleInputChange}
                                placeholder="e.g., 47,000"
                                required
                                isInvalid={!!errors.price}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.price}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Image URL</Form.Label>
                            <Form.Control
                                type="text"
                                name="image_url"
                                value={formData.image_url}
                                onChange={handleInputChange}
                                placeholder="https://example.com/image.jpg"
                                isInvalid={!!errors.image_url}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.image_url}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Features</Form.Label>
                            <div className="d-flex gap-2 mb-2">
                                <Form.Control
                                    type="text"
                                    value={newFeature}
                                    onChange={(e) => setNewFeature(e.target.value)}
                                    placeholder="Add a feature"
                                />
                                <Button variant="secondary" onClick={handleAddFeature}>Add</Button>
                            </div>
                            {formData.features.length > 0 && (
                                <ul className="list-group">
                                    {formData.features.map((feature, index) => (
                                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                            {feature}
                                            <Button variant="outline-danger" size="sm" onClick={() => handleRemoveFeature(index)}>
                                                Remove
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Sort Order</Form.Label>
                            <Form.Control
                                type="number"
                                name="sort_order"
                                value={formData.sort_order}
                                onChange={handleInputChange}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                        <Button variant="primary" type="submit">
                            {editingVariant ? 'Update' : 'Add'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default VehicleVariants;
